import React, { useContext, useEffect, useState } from 'react';

import Bugsnag from '@bugsnag/js';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from 'styled-components';

import AppContainer from 'components/AppContainer/AppContainer';
import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import Loading from 'components/Loading/Loading';
import Popup from 'components/Popup/Popup';
import RetryingIndicator from 'components/RetryingIndicator';
import { ERROR_PATHS, ERROR_ROUTE } from 'constants/env';
import { getTheme } from 'constants/theme';
import './Root.css';
import Splash from 'containers/Splash/Splash';
import { useApi } from 'modules/Api/ApiContext';
import { TOKEN_RETRY_PHASE } from 'modules/Api/tokenRetry';
import Routes from 'modules/Routes/Routes';
import { AppContext } from 'store/AppContext';
import { extractParams, fetchInitialData } from 'utils/common/common';

const getTokenRetryViewState = ({
    tokenRetryPhase,
    showSplash,
    loading,
    isErrorRoute,
}) => {
    const isTokenBackoffActive = tokenRetryPhase !== TOKEN_RETRY_PHASE.IDLE;

    return {
        shouldShowRetryingIndicator:
            tokenRetryPhase === TOKEN_RETRY_PHASE.VISIBLE && !isErrorRoute,
        shouldShowSplash: showSplash && !isTokenBackoffActive,
        shouldShowLoading:
            !showSplash && loading && !isTokenBackoffActive && !isErrorRoute,
        shouldShowApp:
            !showSplash &&
            (!isTokenBackoffActive || isErrorRoute) &&
            (!loading || isErrorRoute),
    };
};

function Root() {
    const {
        userData,
        setNewUserData,
        updateUserData,
        setHidePopup,
        setWMAAllowed,
        setFilterNonurgent,
        setEnableContinueBtn,
        setShowContinueBtn,
    } = useContext(AppContext);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { partnerId, requestId } = extractParams(pathname);
    const [showSplash, setShowSplash] = useState(true);
    const [loading, setIsLoading] = useState(Boolean(partnerId));
    const { fetchData, tokenRetryPhase } = useApi();
    const { t } = useTranslation();
    const isErrorRoute = pathname?.startsWith(ERROR_ROUTE);
    const {
        shouldShowRetryingIndicator,
        shouldShowSplash,
        shouldShowLoading,
        shouldShowApp,
    } = getTokenRetryViewState({
        tokenRetryPhase,
        showSplash,
        loading,
        isErrorRoute,
    });

    useEffect(() => {
        Bugsnag.leaveBreadcrumb(`App loaded with path: ${pathname}.`);
        if (partnerId) {
            fetchInitialData({
                partnerId,
                requestId,
                setIsLoading,
                fetchData,
                updateUserData,
                setNewUserData,
                navigate,
                t,
                setHidePopup,
                setWMAAllowed,
                setFilterNonurgent,
                setEnableContinueBtn,
                setShowContinueBtn,
            });
        } else if (pathname?.startsWith(ERROR_ROUTE)) {
            setIsLoading(false);
            navigate(pathname);
        } else {
            setIsLoading(false);
            navigate(ERROR_PATHS.INVALID_URL);
        }

        const splashTimeout = setTimeout(() => {
            setShowSplash(false);
        }, 1500);

        return () => clearTimeout(splashTimeout);
    }, []);

    return (
        <ThemeProvider theme={getTheme(userData?.template?.content || {})}>
            <AppContainer data-testid="m7sqvznq-qvvupr">
                {shouldShowSplash && <Splash data-testid="m7sqvznq-4bq1bz" />}
                {shouldShowRetryingIndicator && <RetryingIndicator />}
                {shouldShowLoading && (
                    <Loading data-testid="m7sqvznq-1ehr4w" />
                )}
                {shouldShowApp && (
                    <>
                        <Header data-testid="m7sqvznq-p2sk75" />
                        <Routes data-testid="m7sqvznq-dumel1" />
                        <ToastContainer data-testid="m7sqvznq-b1i4sj" />
                        <Footer data-testid="m7sqvznq-hil7zx" />
                        <Popup />
                    </>
                )}
            </AppContainer>
        </ThemeProvider>
    );
}

export default Root;
